import * as RadixTooltip from '@radix-ui/react-tooltip';
import { useState, type ReactElement, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { stopPortalClick } from './portal-events';
import { usePortalTheme } from './use-portal-theme';

export const TooltipProvider = RadixTooltip.Provider;

export interface TooltipProps {
  content: ReactNode;
  /** A single focusable element. It stays the trigger — no wrapper is inserted around it. */
  children: ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  /**
   * Holder tooltipen lukket uten å ta den ut av treet. En knapp som av og til har en grunn
   * (`Button`s `disabledReason`) skal ikke bytte tre når grunnen kommer og går — da ville React
   * montert knappen på nytt, og fokuset og en peker over den ville falt av.
   */
  disabled?: boolean;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  className,
  disabled = false,
}: TooltipProps): ReactNode {
  const portal = usePortalTheme();
  const [open, setOpen] = useState(false);

  return (
    <RadixTooltip.Root
      open={open && !disabled}
      onOpenChange={(next) => {
        setOpen(next && !disabled);
        if (next) portal.capture();
      }}
    >
      <RadixTooltip.Trigger asChild ref={portal.anchorRef}>
        {children}
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          // Portalled to document.body, so it is told which theme (all four axes) it belongs to.
          {...portal.attributes}
          onClick={stopPortalClick}
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'glass-popover z-50 max-w-64 px-2.5 py-1.5',
            'text-xs leading-snug text-default',
            className,
          )}
        >
          {/* Ingen pil i Glass 2: en fylt pil kan ikke ha glassets uskarphet og ble en flekk. */}
          {content}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}
